import { useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Title, useNotify, useRedirect, useShowController } from "react-admin";
import { useSearchParams } from "react-router-dom";
import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate } from "@/utils/date-format";
import { toUtcYmd } from "@/utils/hall-slot-matches-raza-event-date";
import {
  STOP_STATUS_COLORS,
  STOP_STATUS_LABELS,
  type HouseVisitStopDetail,
  type HouseVisitStopStatus,
} from "../types";

const SUBMIT_CHOICES: Array<{
  status: Exclude<HouseVisitStopStatus, "PENDING">;
  label: string;
  color: "success" | "warning" | "inherit";
}> = [
  { status: "VISITED", label: "Visited", color: "success" },
  { status: "NOT_HOME", label: "Not home", color: "inherit" },
  { status: "SKIPPED", label: "Skipped", color: "warning" },
];

type ApiDetail = { rows?: HouseVisitStopDetail[]; count?: number };
type DetailTab = "house" | "family" | "jamaatDues";

const HouseLine = ({ label, value }: { label: string; value?: string | null }) => {
  if (value == null || String(value).trim() === "") return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "96px 1fr", sm: "120px 1fr" },
        gap: 1,
        py: 0.85,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
};

type OutcomePanelProps = {
  status: Exclude<HouseVisitStopStatus, "PENDING"> | null;
  remarks: string;
  submitting: boolean;
  onStatus: (s: Exclude<HouseVisitStopStatus, "PENDING">) => void;
  onRemarks: (v: string) => void;
  onSubmit: () => void;
  compact?: boolean;
};

const OutcomeFields = ({
  status,
  remarks,
  submitting,
  onStatus,
  onRemarks,
  onSubmit,
  compact,
}: OutcomePanelProps) => (
  <Stack spacing={compact ? 1.25 : 1.5}>
    <Stack spacing={1}>
      {SUBMIT_CHOICES.map((c) => {
        const selected = status === c.status;
        return (
          <Button
            key={c.status}
            fullWidth
            size="large"
            color={c.color}
            variant={selected ? "contained" : "outlined"}
            onClick={() => onStatus(c.status)}
            sx={{
              minHeight: compact ? 48 : 52,
              fontWeight: 700,
              justifyContent: "center",
              borderWidth: selected ? 2 : 1,
            }}
          >
            {c.label}
          </Button>
        );
      })}
    </Stack>
    <TextField
      label="Remarks"
      value={remarks}
      onChange={(e) => onRemarks(e.target.value)}
      multiline
      minRows={compact ? 2 : 3}
      fullWidth
    />
    <Button
      fullWidth
      size="large"
      variant="contained"
      disabled={!status || submitting}
      onClick={onSubmit}
      sx={{ minHeight: 48, fontWeight: 700 }}
    >
      {submitting ? "Saving…" : "Save outcome"}
    </Button>
  </Stack>
);

const VisitForm = () => {
  const { record, isPending: showPending } = useShowController();
  const notify = useNotify();
  const redirect = useRedirect();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });

  const stopId = record?.id != null ? String(record.id) : "";
  const [detail, setDetail] = useState<HouseVisitStopDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<Exclude<HouseVisitStopStatus, "PENDING"> | null>(null);
  const [remarks, setRemarks] = useState("");
  const [tab, setTab] = useState<DetailTab>("house");
  const [outcomeOpen, setOutcomeOpen] = useState(false);

  useEffect(() => {
    if (!stopId) return;
    let cancelled = false;
    setLoading(true);
    callApi({
      location: "houseVisitStops",
      method: "GET",
      id: `${stopId}/detail`,
    })
      .then((res) => {
        if (cancelled) return;
        const body = res.data as ApiDetail;
        const row = body.rows?.[0] ?? null;
        setDetail(row);
        if (row?.remarks) setRemarks(String(row.remarks));
        if (row?.status && row.status !== "PENDING") {
          setStatus(row.status as Exclude<HouseVisitStopStatus, "PENDING">);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) notify(err.message || "Failed to load visit detail", { type: "warning" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [notify, stopId]);

  const routeDate = useMemo(() => {
    const fromQuery = toUtcYmd(searchParams.get("date") ?? "");
    if (fromQuery) return fromQuery;
    return toUtcYmd(detail?.slotDate ?? "") ?? null;
  }, [detail?.slotDate, searchParams]);

  const goBackToRoute = () => {
    redirect(
      routeDate ? `/houseVisitDaily?date=${encodeURIComponent(routeDate)}` : "/houseVisitDaily"
    );
  };

  const submit = async () => {
    if (!stopId || !status) {
      notify("Choose a visit status", { type: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      await callApi({
        location: "houseVisitStops",
        method: "PUT",
        id: `${stopId}/submit`,
        data: { status, remarks: remarks.trim() || null },
      });
      notify("Visit saved", { type: "info" });
      setOutcomeOpen(false);
      goBackToRoute();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to submit visit";
      notify(message, { type: "warning" });
    } finally {
      setSubmitting(false);
    }
  };

  const familyCount = detail?.familyMembers?.length ?? 0;
  const sortedFamily = useMemo(() => {
    const members = detail?.familyMembers ?? [];
    return members.slice().sort((a, b) => {
      const aHof = a.HOF_FM_TYPE === "HOF" ? 0 : 1;
      const bHof = b.HOF_FM_TYPE === "HOF" ? 0 : 1;
      if (aHof !== bHof) return aHof - bHof;
      return String(a.Full_Name || "").localeCompare(String(b.Full_Name || ""));
    });
  }, [detail?.familyMembers]);

  if (showPending || loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Visit not found.</Typography>
        <Button sx={{ mt: 1 }} onClick={goBackToRoute}>
          Back to route
        </Button>
      </Box>
    );
  }

  const currentStatus = (detail.status || "PENDING") as HouseVisitStopStatus;
  const hof = detail.hof;
  const savedRemarks =
    typeof detail.remarks === "string" && detail.remarks.trim() ? detail.remarks.trim() : null;
  const outcomeLabel =
    currentStatus === "PENDING"
      ? "Record outcome"
      : `Update outcome · ${STOP_STATUS_LABELS[currentStatus]}`;

  const detailTabs = (
    <>
      <Tabs
        value={tab}
        onChange={(_e, v: DetailTab) => setTab(v)}
        variant={isDesktop ? "standard" : "fullWidth"}
        sx={{
          mb: 1.5,
          minHeight: 48,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": { minHeight: 48, textTransform: "none", fontWeight: 600 },
        }}
      >
        <Tab
          value="house"
          icon={<HomeWorkOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="House"
        />
        <Tab
          value="family"
          icon={<PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label={`Family${familyCount ? ` (${familyCount})` : ""}`}
        />
        <Tab
          value="jamaatDues"
          icon={<PaymentsOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Jamaat dues"
        />
      </Tabs>

      {tab === "house" ? (
        <Paper variant="outlined" sx={{ px: { xs: 1.5, md: 2 }, py: 0.5 }}>
          <HouseLine label="Address" value={hof?.Address} />
          <HouseLine label="Building" value={hof?.Building} />
          <HouseLine label="Street" value={hof?.Street} />
          <HouseLine label="Area" value={hof?.Area} />
          <HouseLine label="Housing" value={hof?.Housing} />
          <HouseLine label="Type of house" value={hof?.Type_of_House} />
          <HouseLine label="Sector" value={hof?.Sector} />
          <HouseLine label="Sub-sector" value={hof?.Sub_Sector} />
          {!hof ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No ITS address on file.
            </Typography>
          ) : null}
        </Paper>
      ) : null}

      {tab === "family" ? (
        familyCount ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 1,
            }}
          >
            {sortedFamily.map((m) => (
              <Paper key={m.ITS_ID} variant="outlined" sx={{ px: 1.75, py: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {m.Full_Name || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[m.Age ? `Age ${m.Age}` : null, m.Gender].filter(Boolean).join(" · ") || "—"}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={m.HOF_FM_TYPE || "FM"}
                    variant="outlined"
                    color={m.HOF_FM_TYPE === "HOF" ? "primary" : "default"}
                  />
                </Stack>
              </Paper>
            ))}
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ p: 2.5, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No family members listed.
            </Typography>
          </Paper>
        )
      ) : null}

      {tab === "jamaatDues" ? (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              px: 1.75,
              py: 1,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Sabil
            </Typography>
          </Box>
          <Box sx={{ px: 1.75, py: 0.5 }}>
            <HouseLine
              label="Last paid"
              value={
                detail.sabil?.lastPaidDate
                  ? formatListDate(detail.sabil.lastPaidDate, { utc: true })
                  : "—"
              }
            />
            <HouseLine label="Type" value={detail.sabil?.sabilType || "—"} />
          </Box>
        </Paper>
      ) : null}
    </>
  );

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: "auto",
        width: "100%",
        pb: { xs: "96px", md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 },
        pt: { xs: 1.5, md: 2.5 },
      }}
    >
      <Title title={detail.hofName || detail.hofItsNo} />

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
        <IconButton
          size="small"
          aria-label="Back to today's route"
          onClick={goBackToRoute}
          sx={{ ml: -0.75 }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          Today&apos;s route
          {routeDate ? ` · ${formatListDate(routeDate, { utc: true })}` : ""}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
          gap: { xs: 1.5, md: 2.5 },
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, mb: 1.5 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" }, letterSpacing: "-0.02em" }}
                >
                  {detail.hofName || hof?.Full_Name || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  ITS {detail.hofItsNo}
                </Typography>
              </Box>
              <Chip
                label={STOP_STATUS_LABELS[currentStatus] ?? currentStatus}
                color={STOP_STATUS_COLORS[currentStatus] ?? "default"}
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
            </Stack>

            {savedRemarks ? (
              <Paper
                elevation={0}
                sx={{
                  mt: 1.75,
                  px: 1.5,
                  py: 1.25,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.35, fontWeight: 700 }}
                >
                  Visit remarks
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {savedRemarks}
                </Typography>
              </Paper>
            ) : null}
          </Paper>

          {detailTabs}
        </Box>

        {isDesktop ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              position: "sticky",
              top: 16,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Visit outcome
            </Typography>
            <OutcomeFields
              status={status}
              remarks={remarks}
              submitting={submitting}
              onStatus={setStatus}
              onRemarks={setRemarks}
              onSubmit={() => {
                void submit();
              }}
            />
          </Paper>
        ) : null}
      </Box>

      {!isDesktop ? (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            p: 1.5,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            zIndex: (t) => t.zIndex.appBar,
          }}
        >
          <Box sx={{ maxWidth: 720, mx: "auto" }}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={() => setOutcomeOpen(true)}
              sx={{ minHeight: 52, fontWeight: 700 }}
            >
              {outcomeLabel}
            </Button>
          </Box>
        </Box>
      ) : null}

      <Dialog
        open={outcomeOpen}
        onClose={() => (!submitting ? setOutcomeOpen(false) : undefined)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            m: { xs: 1.5, sm: 2 },
            width: { xs: "calc(100% - 24px)", sm: "100%" },
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Visit outcome</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {detail.hofName || hof?.Full_Name || detail.hofItsNo}
          </Typography>
          <OutcomeFields
            status={status}
            remarks={remarks}
            submitting={submitting}
            onStatus={setStatus}
            onRemarks={setRemarks}
            onSubmit={() => {
              void submit();
            }}
            compact
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button
            onClick={() => setOutcomeOpen(false)}
            disabled={submitting}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisitForm;
